import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask, restartableTask } from 'ember-concurrency';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-common/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import {
  querySettingsRequest,
  upsertSettingRequest,
  replaceSettingValueRequest,
  type SettingResource,
} from '../requests/setting.ts';

export const PINNED_REPORTS_SUBJECT = 'tio-admin:reports';
const PINNED_REPORTS_PREDICATE = 'pinned-reports';
/** Hard cap (design shows 8 cards): pinning a 9th evicts the oldest pin. */
export const MAX_PINNED_REPORTS = 8;

/**
 * Per-admin report preferences, persisted server-side in tio_types.settings
 * (owner = the admin's User; value = `{ reportIds: [...] }`, newest pin
 * first). Pins update optimistically and roll back to the last known
 * server state on failure; an authorization failure (settings CRUD is
 * super-admin gated) disables pinning for the session instead of surfacing
 * errors. Pinning stays disabled until the initial load succeeds so a
 * first write can never clobber a not-yet-loaded server-side list.
 */
export default class ReportsPreferencesService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;

  /** Ordered pinned report ids, newest pin first. */
  @tracked pinnedReportIds: string[] = [];
  /** Owner whose pins are currently loaded — the singleton survives
   * logout/login without app teardown, so all state is scoped per owner. */
  @tracked private loadedForOwner: string | null = null;
  /** Owner whose settings access was denied (403). */
  @tracked private deniedForOwner: string | null = null;

  #settingId: string | null = null;
  /** Last state confirmed by the server; rollback target on failed writes. */
  #serverIds: string[] = [];
  /** Bumped on every local mutation; failed writes only roll back when no
   * newer mutation has happened (else the queued persist owns the state). */
  #revision = 0;

  /**
   * The settings predicate this service reads/writes. Reports pin under
   * 'pinned-reports'; a subclass (dashboards) overrides it so its pins live in
   * a separate settings row under the shared PINNED_REPORTS_SUBJECT.
   */
  protected get pinnedPredicate() {
    return PINNED_REPORTS_PREDICATE;
  }

  get pinningUnavailable() {
    return Boolean(this.ownerId) && this.deniedForOwner === this.ownerId;
  }

  get canPin() {
    return (
      Boolean(this.ownerId) && this.loadedForOwner === this.ownerId && !this.pinningUnavailable
    );
  }

  get ownerId() {
    const id = this.sessionContext.user?.id;
    return id ? String(id) : null;
  }

  /**
   * Idempotent per owner: both pages call this; only the first performs the
   * fetch for the signed-in admin. A different owner (re-login without app
   * teardown) drops the previous admin's state and loads fresh so pins are
   * never shown to — or written for — the wrong account. A 403 marks pinning
   * unavailable for that owner — don't re-issue the forbidden request on
   * every page instantiation. restartable (not drop) so a load performed for
   * a NEW owner cancels an in-flight load for the previous one instead of
   * being discarded.
   */
  load = restartableTask(async () => {
    const ownerId = this.ownerId;
    if (!ownerId || this.loadedForOwner === ownerId || this.deniedForOwner === ownerId) return;

    this.pinnedReportIds = [];
    this.#settingId = null;
    this.#serverIds = [];
    this.#revision += 1;

    try {
      const stored = await this.readPins(ownerId);
      // The session may have switched again while the request was in flight;
      // never apply another owner's rows.
      if (this.ownerId !== ownerId) return;
      if (stored.settingId) {
        this.#settingId = stored.settingId;
        // Trim to the cap so over-long lists (older versions, manual edits)
        // can't strand hidden, un-unpinnable entries.
        this.pinnedReportIds = Array.isArray(stored.ids)
          ? stored.ids.map(String).slice(0, MAX_PINNED_REPORTS)
          : [];
      }
      this.#serverIds = this.pinnedReportIds;
      this.loadedForOwner = ownerId;
    } catch (error) {
      this.#handleError(error, { notify: false, ownerId });
    }
  });

  isPinned = (reportId: string | null) => this.pinnedReportIds.includes(String(reportId));

  togglePin = (reportId: string | null) => {
    if (this.isPinned(reportId)) {
      this.unpin(reportId);
    } else {
      this.pin(reportId);
    }
  };

  pin = (reportId: string | null) => {
    if (!this.canPin) return;
    const id = String(reportId);
    if (this.pinnedReportIds.includes(id)) return;
    this.pinnedReportIds = [id, ...this.pinnedReportIds].slice(0, MAX_PINNED_REPORTS);
    this.#revision += 1;
    void this.persist.perform();
  };

  unpin = (reportId: string | null) => {
    if (!this.canPin) return;
    const id = String(reportId);
    if (!this.pinnedReportIds.includes(id)) return;
    this.pinnedReportIds = this.pinnedReportIds.filter((pinned) => pinned !== id);
    this.#revision += 1;
    void this.persist.perform();
  };

  /**
   * keepLatest so rapid pin/unpin bursts coalesce into one trailing write of
   * the final list instead of racing concurrent upserts.
   */
  persist = keepLatestTask(async () => {
    const ownerId = this.ownerId;
    // Never write for an owner whose state isn't the one loaded (re-login
    // race): #settingId could still belong to the previous admin.
    if (!ownerId || this.loadedForOwner !== ownerId) return;
    const revision = this.#revision;
    const reportIds = this.pinnedReportIds;
    try {
      const settingId = await this.writePins(ownerId, reportIds, this.#settingId);
      // Re-check AFTER the await: if another admin loaded while this write
      // was in flight, restoring the old setting id would point their next
      // pin at the previous admin's row.
      if (this.loadedForOwner !== ownerId) return;
      this.#settingId = settingId;
      this.#serverIds = reportIds;
    } catch (error) {
      if (this.loadedForOwner === ownerId) {
        // Only roll back when this failed write was for the CURRENT state; if
        // the user pinned/unpinned meanwhile, the queued persist owns it and
        // a rollback here would erase their newer change.
        if (this.#revision === revision) {
          this.pinnedReportIds = this.#serverIds;
        }
      }
      this.#handleError(error, { notify: true, ownerId });
    }
  });

  /**
   * The stored pins for `ownerId`. Override together with {@link writePins} to move the
   * pins to a different store; every guard in `load` and `persist` still applies.
   *
   * @returns `settingId` null when no row exists yet; `ids` exactly as stored, unvalidated
   */
  protected async readPins(ownerId: string): Promise<{ settingId: string | null; ids: unknown }> {
    const { content } = await this.store.request(
      querySettingsRequest({
        ownerType: 'User',
        ownerId,
        subject: PINNED_REPORTS_SUBJECT,
        predicate: this.pinnedPredicate,
      })
    );
    const row = (content as { data?: SettingResource[] })?.data?.[0];
    return { settingId: row?.id ?? null, ids: row?.attributes?.value?.['reportIds'] };
  }

  /**
   * Stores `reportIds` for `ownerId`, replacing the row named by `settingId` when there is one.
   *
   * @returns the row id to pass back on the next write
   */
  protected async writePins(
    ownerId: string,
    reportIds: string[],
    settingId: string | null
  ): Promise<string | null> {
    const value = { reportIds };
    if (settingId) {
      await this.store.request(replaceSettingValueRequest(settingId, value));
      return settingId;
    }
    const { content } = await this.store.request(
      upsertSettingRequest({
        ownerResourceType: 'users',
        ownerId,
        subject: PINNED_REPORTS_SUBJECT,
        predicate: this.pinnedPredicate,
        value,
      })
    );
    return (content as { data?: SettingResource })?.data?.id ?? null;
  }

  #handleError(error: unknown, { notify, ownerId }: { notify: boolean; ownerId: string }) {
    if (isForbidden(error)) {
      // Denial belongs to the owner whose request 403'd — never the admin
      // who happens to be signed in when the response lands.
      this.deniedForOwner = ownerId;
      return;
    }
    console.error('[reports-preferences] settings request failed', error);
    if (notify && this.ownerId === ownerId) {
      this.notifications.add('Failed to save pinned reports', { appearance: 'error' });
    }
  }
}

export function isForbidden(error: unknown) {
  const candidate = error as {
    status?: number | string;
    response?: { status?: number };
    errors?: Array<{ status?: number | string }>;
  } | null;
  const statuses = [
    candidate?.status,
    candidate?.response?.status,
    ...(candidate?.errors?.map((e) => e.status) ?? []),
  ];
  return statuses.some((status) => Number(status) === 403);
}
